/**
 * Simple in-memory rate limiter (single-instance).
 * For multi-instance deployments, swap to Redis.
 */

interface RateLimitEntry {
	count: number;
	resetTime: number;
}

interface RateLimitConfig {
	/** Max requests per window (default: 10) */
	maxRequests?: number;
	/** Window duration in ms (default: 60_000 = 1 min) */
	windowMs?: number;
}

interface RateLimitResult {
	allowed: boolean;
	remaining: number;
	resetIn: number;
	/** Ende des Fensters, absolut. `release` braucht es, um nicht ein spaeteres Fenster zu treffen. */
	resetTime: number;
}

/** Expired entries are otherwise only overwritten on re-use; prune above this
 * bound so rotating/spoofed identifiers cannot grow the map without limit. */
const PRUNE_THRESHOLD = 10_000;

export interface RateLimiter {
	(identifier: string): RateLimitResult;
	/**
	 * Gibt einen gerade verbrauchten Platz zurück.
	 *
	 * Der Zähler läuft, bevor irgendjemand weiss, was die Anfrage kostet. Für
	 * Routen, die eine Ablehnung aussprechen können, die der Aufrufer nicht zu
	 * verantworten hat, ist das falsch herum: dort zählt erst die Antwort, ob
	 * der Versuch echt war. `withRateLimit({ countResponse })` benutzt das.
	 *
	 * `resetTime` ist das Fensterende aus dem `RateLimitResult` derselben Anfrage.
	 * Es gibt bewusst keine Korrektur über Fensterwechsel hinweg: gehört der Platz
	 * zu einem anderen Fenster, gibt es nichts mehr zurückzugeben.
	 */
	release(identifier: string, resetTime: number): void;
}

export function createRateLimiter(config: RateLimitConfig = {}): RateLimiter {
	const maxRequests = config.maxRequests ?? 10;
	const windowMs = config.windowMs ?? 60_000;
	const store = new Map<string, RateLimitEntry>();

	function checkRateLimit(identifier: string): RateLimitResult {
		const now = Date.now();

		if (store.size >= PRUNE_THRESHOLD) {
			for (const [key, candidate] of store) {
				if (candidate.resetTime < now) store.delete(key);
			}
		}

		const entry = store.get(identifier);

		if (!entry || entry.resetTime < now) {
			const resetTime = now + windowMs;
			store.set(identifier, { count: 1, resetTime });
			return { allowed: true, remaining: maxRequests - 1, resetIn: windowMs, resetTime };
		}

		if (entry.count >= maxRequests) {
			return { allowed: false, remaining: 0, resetIn: entry.resetTime - now, resetTime: entry.resetTime };
		}

		entry.count++;
		return {
			allowed: true,
			remaining: maxRequests - entry.count,
			resetIn: entry.resetTime - now,
			resetTime: entry.resetTime,
		};
	}

	checkRateLimit.release = (identifier: string, resetTime: number): void => {
		const entry = store.get(identifier);
		// Nur das Fenster, das den Platz auch verbraucht hat. Eine lang laufende
		// Anfrage kann ueber die Fenstergrenze zurueckkehren; ohne diesen Vergleich
		// wuerde sie den Zaehler des NEUEN Fensters senken und dort eine fremde,
		// erfolgreiche Bestellung ungezaehlt machen.
		if (!entry || entry.resetTime !== resetTime) return;
		if (entry.count <= 1) store.delete(identifier);
		else entry.count--;
	};

	return checkRateLimit;
}
